import shortid from "shortid"
import merge from "lodash.merge"

import Keyset from "@frog/source/utility/Keyset.js"

import Stage from "@frog/source/models/Stage.js"
import Step from "@frog/source/models/Step.js"
import Geometry from "@frog/source/utility/Geometry.js"

import Speedtest from "@frog/source/utility/Speedtest.js"

const MAX_INFLUENCE = 200 // this is duped in Stage.js
const INFLUENCE_ITERATIONS = 10
const INFLUENCE_DECAY = 20

import skills from "@frog/source/data/skills.js"
import units from "@frog/source/data/units.js"

import World  from "@frog/source/models/World.js"

function findUnitByPosition(stage, position) {
    return Keyset.find(stage.units, (unit) => Geometry.equals(unit.position, position))
}

export default class Unit {
    static instantiate(unit) {
        if(unit.skills instanceof Array == false) {
            unit.skills = [unit.skills]
        }
        let referenceUnit = {}
        if(unit.reference != undefined && unit.reference != "") {
            referenceUnit = units[unit.reference] || {}
        }
        // else if(unit.family != undefined) {
        //     unit.reference = "family:" + unit.family + "/level:" + (unit.level || 1)
        //     referenceUnit = units[unit.reference] || {}
        // }
        const baseUnit = {
            "key": "unit:" + shortid.generate(),
            "energy": {"move": true, "invoke skill": true},
            // "maxhealth": 5,
            // "power": 1,
            // "moverange": 2,
            // "image": require("@frog/source/images/blob.png"),
            "effects": {},
        }
        unit.health = unit.health !== undefined ? unit.health : unit.maxhealth
        referenceUnit.health = referenceUnit.health !== undefined ? referenceUnit.health : referenceUnit.maxhealth
        return merge({}, baseUnit, referenceUnit, unit)
    }
    static computeInfluenceMap(affiliation, stage) {
        let influence = {}

        Keyset.forEach(stage.units, (unit) => {
            if(unit.affiliation != affiliation
            && unit.affiliation != "none"
            && unit.isDead != true) {
                // influence[unit.position.x + "x" + unit.position.y + "x" + unit.position.z] = MAX_INFLUENCE
                influence[unit.position.x + "x" + unit.position.y] = MAX_INFLUENCE
            }
        })

        for(let i = 0; i < INFLUENCE_ITERATIONS; i += 1) {
            let nextinfluence = {}
            for(let x = 0; x < stage.world.width; x += 1) {
                for(let y = 0; y < stage.world.height; y += 1) {
                    if(stage.world.terrain[x + "x" + y] == undefined) {
                        continue
                    }
                    const z = stage.world.terrain[x + "x" + y].position.z

                    // if(World.hasCollision(stage.world, {x, y, z})) {
                    //     // Skip this, because there is collision here!!
                    //     continue
                    // }
                    // nextinfluence[x + "x" + y + "x" + z] = Math.max(
                    //     (influence[x + "x" + y + "x" + z] || 0),
                    //     (influence[(x + 1) + "x" + y + "x" + z] || 0) - INFLUENCE_DECAY,
                    //     (influence[(x - 1) + "x" + y + "x" + z] || 0) - INFLUENCE_DECAY,
                    //     (influence[x + "x" + (y + 1) + "x" + z] || 0) - INFLUENCE_DECAY,
                    //     (influence[x + "x" + (y - 1) + "x" + z] || 0) - INFLUENCE_DECAY
                    // )

                    const currentPosition = {x, y, z}
                    const influences = Geometry.getNeighborPoints(currentPosition)
                        .filter((nextPosition) => {
                            const unit = findUnitByPosition(stage, nextPosition)
                            if(unit != undefined
                            && unit.affiliation == "none") {
                                return false
                            }
                            return true
                        })
                        .filter((nextPosition) => World.hasCollision(stage.world, currentPosition, nextPosition) != true)
                        .map((nextPosition) => (influence[nextPosition.x + "x" + nextPosition.y] || 0) - INFLUENCE_DECAY)
                        .concat(influence[currentPosition.x + "x" + currentPosition.y] || 0)
                    nextinfluence[x + "x" + y] = Math.max(...influences)

                    // const topPosition = {x, y, z}
                    // const influences = Geometry.getNeighborPoints(topPosition)
                    //     .filter((position) => World.hasCollision(stage.world, position) != true)
                    //     .map((position) => (influence[position.x + "x" + position.y + "x" + position.z] || 0) - INFLUENCE_DECAY)
                    //     .concat(influence[topPosition.x + "x" + topPosition.y + "x" + topPosition.z] || 0)
                    // nextinfluence[x + "x" + y + "x" + z] = Math.max(...influences)
                }
            }
            influence = nextinfluence
        }

        return influence
    }

    // This function will return an actionmap, a
    // hash of all the possible actions the given
    // unit could perform within range. An "action"
    // can be a move, an attack, an assist, etc.
    static calculateActions(performerKey, stage) {
        const mark = Speedtest.mark("Actions", "Unit.calculateActions")

        if(stage == undefined) throw new Error("requires action.stage")
        if(performerKey == undefined) throw new Error("requires action.performerKey")

        const startingAction =  {}

        const performer = stage.units[performerKey]
        if(performer == undefined) {
            throw new Error("Could not find unit " + performerKey + " in the stage.")
        }

        if(performer.isDead) {
            return
        }

        startingAction.performerKey = performerKey
        startingAction.isStartingAction = true
        startingAction.type = performer.energy["move"] == true ? "wait-for-move" : "wait-for-attack"
        startingAction.position = performer.position
        startingAction.alphastage = startingAction.omegastage = Stage.copy(stage)
        startingAction.distance = performer.energy["move"] == true ? 0 : performer.moverange
        startingAction.steps = []

        const submark = Speedtest.mark("Unit.calculateAction", "influence map")
        startingAction.influenceMap = Unit.computeInfluenceMap(performer.affiliation, startingAction.alphastage)
        submark()

        const actionmap = {}

        Unit.populateActionMap(actionmap, startingAction)
        Unit.pruneInternalActionMapData(actionmap)

        mark()
        return actionmap
    }
    static populateActionMap(actionmap, stubAction) {
        const calculatedAction = Unit.calculateAction(stubAction)
        if(calculatedAction == undefined) return
        Unit.saveAction(actionmap, calculatedAction)
        calculatedAction.nextActions.forEach((nextAction) => {
            Unit.populateActionMap(actionmap, nextAction)
        })
    }
    static calculateAction(action) {
        const mark1 = Speedtest.mark("Unit.calculateAction", "initialization")

        if(action.type === undefined) throw new Error("requires action.type")
        if(action.position === undefined) throw new Error("requires action.position")
        if(action.performerKey === undefined) throw new Error("requires action.performerKey")
        if(action.alphastage === undefined) throw new Error("requires action.alphastage")

        action.alphastage = Stage.copy(action.alphastage)

        // If nothing else happens, the omegastage
        // will match the alphastage. But throughout
        // this action, the omegastage will be cloned
        // and edited via the game logic.
        action.omegastage = action.alphastage

        action.positions = [
            {"x": action.position.x, "y": action.position.y, "z": action.position.z, "type": action.type}
        ]
        action.steps = []

        if(action.previousAction != undefined) {
            action.positions = action.positions.concat(action.previousAction.positions)

            // TODO: we really reusing these and not recomputing these?
            action.steps = action.steps.concat(action.previousAction.steps.filter((step) => {
                return step.type != "exhaust energy"
                    && step.type != "increment stage.actionMark"
            }))
            // if(action.steps.length > 0) {
            //     action.omegastage = action.steps[action.steps.length-1].omegastage
            // }
            // TODO: YOU REALLY CAN't JUST FILTER THE STEPS AND NOT ASSUME THE OMEGASTAGE
            // IS ALSO FILTERED; IT WAS BUIlT ON EACH OF THOSE STEPS IN TURN, SO IT IS
            // STILL TRUE EVEN IF YOU REMOVE THE STEPS!!
        }

        mark1()

        ///////////////////////////////
        // Unit-to-World Collision  //
        /////////////////////////////

        const mark2 = Speedtest.mark("Unit.calculateAction", "unit-to-world collision")

        const isArcingAttack = (
            action.type == "invoke skill"
            && action.skillKey != undefined
            && skills.get(action.skillKey) != undefined
            && skills.get(action.skillKey).range.type == "arc"
        )

        const previousPosition = action.positions[1] || action.position
        const nextPosition = action.positions[0] || action.position

        if(isArcingAttack == true) {
            if(World.isInBounds(action.alphastage.world, nextPosition)) {
                mark2()
                return
            }
            const nextTerrain = World.getTerrain(action.alphastage.world, nextPosition)
            if(nextTerrain.shape == "obstruction") {
                mark2()
                return
            }
            action.position.z = nextTerrain.position.z
        }

        if(isArcingAttack == false) {
            if(World.hasCollision(action.alphastage.world, previousPosition, nextPosition)) {
                mark2()
                return
            }

            const previousTerrain = World.getTerrain(action.alphastage.world, previousPosition)
            const nextTerrain = World.getTerrain(action.alphastage.world, nextPosition)
            const zDistance = Math.abs(previousTerrain.position.z - nextTerrain.position.z)
            if(zDistance <= 0.5) {
                action.position.z = nextTerrain.position.z
            } else if(zDistance <= 1 && action.distance == 1) {
                action.position.z = nextTerrain.position.z
                action.distance = 100
            }
        }

        mark2()

        /////////////////////////////
        // Unit-to-Unit Collision //
        ///////////////////////////

        const mark3 = Speedtest.mark("Unit.calculateAction", "unit-to-unit collision")

        const performer = action.alphastage.units[action.performerKey]
        // Scan for any units at this position.
        Keyset.forEach(action.alphastage.units, (unit) => {
            if(unit.key != action.performerKey
            && unit.isDead != true
            && unit.position.x == action.position.x
            && unit.position.y == action.position.y
            && unit.position.z == action.position.z) {
                if(action.type == "move") {
                    if(unit.affiliation == performer.affiliation
                    && unit.effects.flinch == undefined) { // TODO: ARE WE HARDCODING TRAP BLOCKING??
                        action.type = "move through"
                    } else {
                        action.type = "no move is unit"
                    }
                }
            }
        })

        if(action.type == "no move is unit") {
            mark3()
            return
        }

        if(action.type == "invoke skill"
        && skills.get(action.skillKey).canTarget instanceof Function
        && skills.get(action.skillKey).canTarget({
            "performerKey": action.performerKey,
            "stage": action.alphastage,
            "position": action.position,
        }) == false) {
            action.type == "no invoke skill can not target"
            mark3()
            return
        }

        mark3()

        ///////////////////////////////
        // Calculate the Omegastage //
        /////////////////////////////

        const mark4 = Speedtest.mark("Unit.calculateAction", "omegastage calculation")

        if(action.type == "wait-for-move") {
            addStep(action, Step.instantiate({
                "type": "exhaust energy",
                "alphastage": action.omegastage,
                "deltastages": [
                    {
                        "units": {
                            [action.performerKey]: {
                                "energy": {"move": false}
                            }
                        }
                    },
                ]
            }))
        }

        if(action.type == "move"
        || action.type == "move through") {
            addStep(action, Step.instantiate({
                "type": "move",
                "alphastage": action.omegastage,
                "deltastages": [
                    {
                        "units": {
                            [action.performerKey]: {
                                "position": {
                                    "mode": "absolute",
                                    "x": action.position.x,
                                    "y": action.position.y,
                                    "z": action.position.z,
                                }
                            }
                        }
                    }
                ]
            }))

            // TRAP //
            const traps = []
            Keyset.forEach(action.omegastage.units, (unit) => {
                if(unit.isDead == true) {
                    return
                }
                Keyset.forEach(unit.effects, (effect, effectKey) => {
                    if(effect.type == "trap"
                    && effect.positions != undefined) {
                        effect.positions.forEach((position) => {
                            if(Geometry.equals(action.position, position)) {
                                traps.push({
                                    "unit": unit,
                                    "position": position,
                                    "actionMark": effect.actionMark,
                                    "skillKey": effect.skillKey,
                                    "effectKey": effectKey,
                                })
                            }
                        })
                    }
                })
            })
            if(traps.length > 1) {
                traps.sort((a, b) => a.actionMark - b.actionMark)
            }
            traps.forEach((trap) => {
                if(action.omegastage.units[trap.unit.key].isDead == true
                || action.omegastage.units[action.performerKey].isDead == true) {
                    return
                }
                if(skills.get(trap.skillKey) == undefined) {
                    return
                }
                addStep(action, Step.instantiate({
                    "type": "invoke skill",
                    "skillKey": trap.skillKey,
                    "alphastage": action.omegastage,
                    "deltastages": skills.get(trap.skillKey).calculateDeltastages({
                        "performerKey": trap.unit.key,
                        "position": trap.position,
                        "stage": action.omegastage,
                        "effectKey": trap.effectKey, // adding this so it can be removed after execution
                    })
                }))

                /////////////
                // DEATHS //
                ///////////
                const deaths = getDeaths(action.omegastage)
                if(deaths.length > 0) {
                    addStep(action, Step.instantiate({
                        "type": "death",
                        "alphastage": action.omegastage,
                        "deltastages": deaths.map((death) => {
                            return {
                                "annotation": {"type": death.type},
                                "units": {[death.unitKey]: {"isDead": true}},
                            }
                        })
                    }))
                }

                // TODO: Can you counter-attack a dangerzone skill?
            })

            // TODO: why is there logic aroundthis? of course we exhaust the energy!!
            // const omegaperformer = action.omegastage.units.find((unit) => unit.key == action.performerKey)
            if(action.type == "move") {
            // && action.distance >= omegaperformer.moverange
            // && omegaperformer.energy.move != true) {
                addStep(action, Step.instantiate({
                    "type": "exhaust energy",
                    "alphastage": action.omegastage,
                    "deltastages": [
                        {
                            "units": {
                                [action.performerKey]: {
                                    "energy": {"move": false}
                                }
                            }
                        },
                    ]
                }))
            }
        }


        if(action.type == "invoke skill"
        && action.skillKey != undefined) {
            let invokestep
            addStep(action, invokestep = Step.instantiate({
                "type": "invoke skill",
                "skillKey": action.skillKey,
                "alphastage": action.omegastage,
                "deltastages": skills.get(action.skillKey).calculateDeltastages({
                    "performerKey": action.performerKey,
                    "position": action.position,
                    "stage": action.omegastage,
                }),
            }))

            // TRAPS TRIGGERED BY UNITS BEING PUSHED //
            // THIS SHOULD BE A REACTIVE SYSTEM, AUTOMATICALLY TRIGGERED WHEN THIS SORT OF STATE CHANGES
            action.steps[action.steps.length - 1].deltastages.forEach((deltastage) => {
                Keyset.forEach(deltastage.units, (deltaunit, unitKey) => {
                    if(deltaunit.position == undefined) {
                        return
                    }
                    const movedUnit = action.omegastage.units[unitKey]
                    const traps = []
                    Keyset.forEach(action.omegastage.units, (trapperUnit) => {
                        if(trapperUnit.isDead == true) {
                            return
                        }
                        Keyset.forEach(trapperUnit.effects, (effect, effectKey) => {
                            if(effect.type == "trap"
                            && effect.positions != undefined) {
                                effect.positions.forEach((position) => {
                                    if(Geometry.equals(movedUnit.position, position)) {
                                        traps.push({
                                            "performer": trapperUnit,
                                            "position": position,
                                            "skillKey": effect.skillKey,
                                            "effectKey": effectKey,
                                        })
                                    }
                                })
                            }
                        })
                    })
                    // TODO: In the case of multiple overlapping traps, use.. the earliest one?
                    // this is solved in the move trap trigger, copy that here in invoke skill trap trigger
                    if(traps[0] != undefined
                    && skills.get(traps[0].skillKey) != undefined) {
                        addStep(action, Step.instantiate({
                            "type": "invoke skill",
                            "skillKey": traps[0].skillKey,
                            "alphastage": action.omegastage,
                            "deltastages": skills.get(traps[0].skillKey).calculateDeltastages({
                                "performerKey": traps[0].performer.key,
                                "position": traps[0].position,
                                "effectKey": traps[0].effectKey, // adding this so it can be removed after execution
                                "stage": action.omegastage,
                            }),
                        }))

                        /////////////
                        // DEATHS //
                        ///////////
                        const deaths = getDeaths(action.omegastage)
                        if(deaths.length > 0) {
                            addStep(action, Step.instantiate({
                                "type": "death",
                                "alphastage": action.omegastage,
                                "deltastages": deaths.map((death) => {
                                    return {
                                        "annotation": {"type": death.type},
                                        "units": {[death.unitKey]: {"isDead": true}},
                                    }
                                })
                            }))
                        }

                        // TODO: Can you counter-attack a dangerzone skill?
                    }
                })
            })

            /////////////
            // DEATHS //
            ///////////
            const deaths = getDeaths(action.omegastage)
            if(deaths.length > 0) {
                addStep(action, Step.instantiate({
                    "type": "death",
                    "alphastage": action.omegastage,
                    "deltastages": deaths.map((death) => {
                        return {
                            "annotation": {"type": death.type},
                            "units": {[death.unitKey]: {"isDead": true}},
                        }
                    })
                }))
            }

            //////////////////////
            // COUNTER-ATTACKS //
            ////////////////////

            // // RANGE DOES NOT RESPECT Z AXIS
            // // RANGE DOES NOT SUPPORT MIN-MAX-RANGE SKILLS
            // TODO: rename deltastage to deltastages so you know it's an array
            const damagedUnits = {}
            invokestep.deltastages.forEach((deltastage) => {
                if(deltastage.units == undefined) {
                    return
                }
                Keyset.forEach(deltastage.units, (deltaunit, unitKey) => {
                    if(deltaunit.health == undefined
                    || deltaunit.health >= 0) {
                        return
                    }
                    damagedUnits[unitKey] = deltaunit // TODO: do we need to track multiple deltaunits
                })
            })
            Keyset.forEach(damagedUnits, (deltaunits, unitKey) => {
                const target = action.omegastage.units[unitKey]
                if(target == undefined
                || target.isDead == true) {
                    return
                }
                const performer = action.omegastage.units[action.performerKey]
                const counterattackSkillKey = target.skills.find((skillKey) => {
                    return skills.get(skillKey).type == "counterattack"
                        && Geometry.isWithinRange(target.position, performer.position, skills.get(skillKey).range) // DOES NOT RESPECT Z
                        // If you ever want this to be a "on damage" rather than a fire-emblem-style counterattack
                        // then remove this Geometry.isWithinRange condition.
                })
                if(counterattackSkillKey == undefined) {
                    return
                }
                // const vector = Geometry.difference(performer.position, target.position)
                // const direction = {"x": 0, "y": 0}
                // if(vector.x == 0 && vector.y != 0) {
                //     direction.y = Math.sign(vector.y)
                // } else if(vector.x != 0 && vector.y == 0) {
                //     direction.x = Math.sign(vector.x)
                // } else if(vector.x != 0 && vector.y != 0) {
                //     if(Math.abs(vector.x) > Math.abs(vector.y)) {
                //         direction.x = Math.sign(vector.x)
                //     } else {
                //         direction.y = Math.sign(vector.y)
                //     }
                // }
                // TODO: read a direction from the deltaunit, so the skills can define
                // the direction of damage instead of this ungrokable math equation.
                // const position = Geometry.add(target.position, direction)
                addStep(action, Step.instantiate({
                    "type": "invoke skill",
                    "skillKey": counterattackSkillKey,
                    "alphastage": action.omegastage,
                    "deltastages": skills.get(counterattackSkillKey).calculateDeltastages({
                        "performerKey": target.key,
                        "position": performer.position,
                        "stage": action.omegastage,
                    }),
                }))
            })

            /////////////
            // DEATHS //
            ///////////
            const deaths2 = getDeaths(action.omegastage)
            if(deaths2.length > 0) {
                addStep(action, Step.instantiate({
                    "type": "death",
                    "alphastage": action.omegastage,
                    "deltastages": deaths2.map((death) => {
                        return {
                            "annotation": {"type": death.type},
                            "units": {[death.unitKey]: {"isDead": true}},
                        }
                    })
                }))
            }

            /////////////
            // ENERGY //
            ///////////
            addStep(action, Step.instantiate({
                "type": "exhaust energy",
                "alphastage": action.omegastage,
                "deltastages": [
                    {
                        "units": {
                            [action.performerKey]: {
                                "energy": {"move": false, "invoke skill": false}
                            }
                        }
                    },
                ]
            }))
        }

        addStep(action, Step.instantiate({
            "type": "increment stage.actionMark",
            "alphastage": action.omegastage,
            "deltastages": [{"actionMark": (action.omegastage.actionMark || 0) + 1}]
        }))

        // /////////////////////
        // // Passive Skills //
        // ///////////////////
        // // NONE OF THESE RESPECT .SKILLKEY (OVER .SKILL)
        // Speedtest.point("apply passives")
        //
        // action.alphastage.units.forEach((shadowunit) => {
        //     if(shadowunit.skills != undefined) {
        //         shadowunit.skills.forEach((skill) => {
        //             if(skill.type == "passive"
        //             && skill.trigger == "reaction") {
        //                 skill.editResults(action, shadowunit)
        //             }
        //         })
        //     }
        // })

        // //////////////////////
        // // Counter-Attacks //
        // ////////////////////
        // // NONE OF THESE RESPECT .SKILLKEY (OVER .SKILL)
        // // DON'T WE ALREADY HAVE THIS UP ABOVE?
        /// /// DOES NOT RESPECT ACTION.PERFORMERKEY
        // Speedtest.point("predict counter-attacks")
        //
        // if(action.type === "attack"
        // && action.skill.canBeCountered != true
        // && action.alphastage.units.target.health + ((action.results && action.results.target && action.results.target.health) || 0) > 0) {
        //     let counterskill = action.target.skills.find((skill) => {
        //         return skill.type == "counterattack"
        //             && skill.canTarget(action.performer, action.target, action, action.stage)
        //     })
        //     if(!!counterskill) {
        //         action.counterAction = {
        //             "type": "attack",
        //             "skill": counterskill,
        //             "target": action.performer,
        //             "performer": action.target,
        //             "results": counterskill.calculateDeltastages(action.performer, action.target, true /*isCounterAction*/, {"units": action.alphastage.units}, action)
        //         }
        //     }
        // }
        //
        // // // TODO: also shadow the counter actions
        // // if(action.counterAction != undefined) {
        // //     if(action.counterAction.results != undefined
        // //     && action.counterAction.results.target != undefined
        // //     && action.counterAction.results.target.health != undefined) {
        // //         shadowunit.health += action.counterAction.results.target.health
        // //     }
        // //     // TODO: am i reading this right? we only predict target health for
        // //     // counteractions. no performer health, no target position.
        // // }

        // TODO: Aggregate all the step results into a common result object.

        // // TODO: Revisit calculating the FUTURE state of next turn if the
        // // players chose to end their turn right after this action, mostly
        // // including end-of-turn effects.
        // startTurnEffects.forEach((effect) => {
        //     const results = effect.calculateDeltastages({
        //         "previousState": action.quantumstate["turn:=0"]
        //     })
        //     Object.keys(results).forEach((key) => {
        //         const result = results[key]
        //         const unit = action.quantumstate["turn:+1:start"].units.find((unit) => unit.key == key)
        //         if(unit != undefined) {
        //             unit.health += result.health
        //             unit.isDead = unit.health <= 0
        //         }
        //     })
        // })
        // action.quantumstate["turn:+1:start"] = {
        //     // "units": action.shadowunits.map((unit) => {
        //     //     return {
        //     //         "key": unit.key,
        //     //         "delta": {"health": 0},
        //     //         "alpha": clonedeep(unit),
        //     //         "omega": {/*to be computed*/}
        //     //     }
        //     // }),
        //     "tiles": clonedeep(action.shadowtiles),
        //     "units": clonedeep(action.shadowunits),
        // }
        mark4()

        ///////////////////
        // FLOW ACTIONS //
        /////////////////

        // This is where we flow to all the neighboring
        // tiles. These checks do not verify whether the
        // unit can move to this tile or act on this tile,
        // but it does propagate the actions to check.

        action.nextActions = []

        const mark7 = Speedtest.mark("Unit.flowActions", "flow: move")
        const omegaperformer = action.omegastage.units[action.performerKey]
        if(action.type == "move"
        || action.type == "wait-for-move"
        || action.type == "move through") {
            if(omegaperformer.isDead != true
            // && omegaperformer.energy["move"] == true
            && action.distance < omegaperformer.moverange
            && Unit.isFlinching(omegaperformer) == false) {
                Geometry.getNeighborPoints(action.position).forEach((neighborposition) => {
                    if(action.positions.find((previousPosition) => Geometry.equals(previousPosition, neighborposition))) {
                        return
                    }
                    action.nextActions.push({
                        "type": "move",
                        "performerKey": action.performerKey,
                        "position": neighborposition,
                        "distance": action.distance + 1,
                        "alphastage": action.omegastage,
                        "previousAction": action, // used for steps and positions
                        "influenceMap": action.influenceMap,
                    })
                })
            }
        }
        mark7()

        const mark8 = Speedtest.mark("Unit.flowActions", "flow: invoke skill")
        if(action.type == "move"
        || action.type == "wait-for-move"
        || action.type == "wait-for-attack") {
            if(omegaperformer.isDead != true
            && omegaperformer.energy["invoke skill"] == true
            && omegaperformer.skills != undefined) {
            // && Unit.isFlinching(omegaperformer) == false) {
                omegaperformer.skills.forEach((skillKey) => {
                    if(skillKey == undefined
                    || skills.get(skillKey) == undefined) {
                        return
                    }
                    if(skills.get(skillKey).type == "attack") {
                        Geometry.getNeighborPoints(action.position, skills.get(skillKey).range).forEach((neighborposition) => {
                            action.nextActions.push({
                                "type": "invoke skill",
                                "skillKey": skillKey,
                                "performerKey": action.performerKey,
                                "position": neighborposition,
                                "distance": action.distance + 1,
                                "alphastage": action.omegastage,
                                "previousAction": action, // used for steps and positions
                                "influenceMap": action.influenceMap,
                            })
                        })
                    }
                })
            }
        }
        mark8()

        return action
    }
    static saveAction(actionmap, action) {
        const mark = Speedtest.mark("Unit.calculateAction", "save the action")

        // What makes each action different, even if they share a position and performer?
        // distance is NOT the same
        // skill + type is NOT the same
        // prev actions are NOT the same
        // (probably more, now. this list is very old)

        action.key = action.type + ":" + action.positions.map(Geometry.toString).reverse().join(">")

        const positionkey = action.position.x + "x" + action.position.y + "x" + action.position.z
        actionmap[positionkey] = actionmap[positionkey] || []
        actionmap[positionkey].push(action)

        mark()
        return actionmap
    }
    static pruneInternalActionMapData(actionmap) {
        Keyset.forEach(actionmap, (actionmaptile) => {
            actionmaptile.forEach((action) => {
                // delete action.distance
                delete action.previousAction
                delete action.nextActions
                delete action.isStartingAction
                // delete action.influenceMap
                // performerKey
                // skillKey
                // type
                // steps
                // omegastage
                // alphastage
                // positions
                // position
            })
        })
        return actionmap
    }

    static copy(unit) {
        return {
            "key": unit.key, // unit's individual unique key
            "reference": unit.reference, // unit's shared base class key
            "isDead": unit.isDead || false,
            "hasEscaped": unit.hasEscaped, // todo: THIS IS VICTORY CONDITION SPECIFIC
            "position": {
                "x": unit.position.x,
                "y": unit.position.y,
                "z": unit.position.z,
            },
            "affiliation": unit.affiliation,
            "skills": unit.skills,
            "energy": {
                "move": unit.energy && unit.energy["move"],
                "invoke skill": unit.energy && unit.energy["invoke skill"],
            },
            "element": unit.element,
            "health": unit.health,
            "power": unit.power,
            "maxhealth": unit.maxhealth,
            "moverange": unit.moverange,
            "image": unit.image,
            "effects": Keyset.map(unit.effects, (effect) => {
                const reeffect = {
                    "type": effect.type,
                    "timer": effect.timer,
                }
                if(effect["range"] !== undefined) reeffect["range"] = effect["range"] // TODO: make this a deeper copy, not a shallow copy
                if(effect["actionMark"] !== undefined) reeffect["actionMark"] = effect["actionMark"]
                if(effect["skillKey"] !== undefined) reeffect["skillKey"] = effect["skillKey"]
                if(effect["positions"] !== undefined) reeffect["positions"] = effect["positions"]
                // WE HAVE TO DO THIS TO SATISFY DUMB TEST COMPARES
                return reeffect
            }),
            "name": unit.name,
            "number": unit.number, // THESE ARE STATIC DECORATOR META VALUES, WHICH WE
            // SHOULD SOMEDAY BE ABLE TO JUST READ FROM A REFERENCED PROTOTYPE PARENT.
            "family": unit.family,
            "level": unit.level,
        }
    }

    //////////////
    // Getters //
    ////////////
    static isFlinching(unit) {
        if(unit.effects != undefined) {
            return !!Keyset.find(unit.effects, (effect) => {
                return effect.type == "flinch"
            })
        }
    }
}
// // THIS IS PRETTY OUT OF DATE
// const exampleAction = {
//     "type": String, // "move"|"attack"|"assist"|"move through"|"invoke skill"
//     "stage": Stage,
//     "performer": Unit,
//     "target": Unit,
//     "position": Point, // position of this action
//     "distance": Number, // shortest orthogonal distance from the starting action.
//     "isStartingAction": Boolean,
//     // THE STATE **BEFORE** THE ACTION IS PERFORMED
//     "alphastage": {
//         "units": [{Unit}, {Unit}, {Unit}],
//         "tiles": [{Tile}, {Tile}, ..., {Tile}],
//     },
//     // THE STATE **AFTER** THE ACTION IS PERFORMED
//     "omegastage": {
//         "units": [{Unit}, {Unit}, {Unit}],
//         "tiles": [{Tile}, {Tile}, ..., {Tile}],
//     },
//     "results": {
//         "target": {
//             "health": -1, // remove one health
//             "position": {"x": -1, "y": 0}, // move left one tile
//         },
//         "peformer": {
//             "health": +1
//         },
//     },
//     "counterAction": Action,
// }

// TODO: hey is it documented anywhere that action.positions are in reverse order, so action.positions[0] is the current position, and action.positions[1] is the last position?

import Delta from "@frog/source/models/Delta.js"

function getDeaths(stage) {
    const unitKeys = []
    Keyset.forEach(stage.units, (unit) => {
        if(unit.isDead == true) {
            return
        }
        if(unit.health <= 0) {
            unitKeys.push({"unitKey": unit.key, "type": "death-by-damage"})
        }
        const nextTerrain = World.getTerrain(stage.world, unit.position)
        if(nextTerrain == undefined
        || nextTerrain.position.z <= 0) {
            unitKeys.push({"unitKey": unit.key, "type": "death-by-falling"})
        }
    })
    return unitKeys
}

function addStep(action, step) {
    action.omegastage = step.omegastage
    action.steps.push(step)
}

// ACTIONS is a single discrete input that the player/ai has
// chosen. They are legal actions to perform from the current
// state: within range, respecting targets, etc. They include
// a list of steps/beats.

// STEPS/BEATS are the execution of an action, or a script.
// They include there own discrete edit onto the world, with
// all the details necessary to perform it. It includes both
// the state BEFORE and AFTER the step is performed. They can
// be overlapped or combined by the director.
